import { AuthCore } from '../core'
import { StorageAdapter, Session, User, EmailVerificationToken } from '../types'
import { EmailVerificationManager, generateVerificationLink, generateResetLink, generateLoginLink } from './verification'
import {
  IEmailProvider,
  EmailConfig,
  EmailVerificationRequest,
  EmailVerificationResult,
  EmailLoginResponse
} from './types'
import { ValidationError, AuthenticationError, NotFoundError, ConflictError } from '../errors'
import { nanoid } from 'nanoid'
import * as argon2 from 'argon2'

/**
 * Email authentication service
 * Handles signup, login, password reset, and email verification
 */
export class EmailAuthService {
  private auth: AuthCore
  private storage: StorageAdapter
  private emailProvider: IEmailProvider
  private tokenManager: EmailVerificationManager
  private config: EmailConfig

  constructor(
    auth: AuthCore,
    storage: StorageAdapter,
    emailProvider: IEmailProvider,
    config: EmailConfig
  ) {
    this.auth = auth
    this.storage = storage
    this.emailProvider = emailProvider
    this.config = config
    this.tokenManager = new EmailVerificationManager(storage, {
      verificationExpiry: config.verificationExpiry || 3600,
      codeLength: config.codeLength || 6,
      maxAttempts: config.maxAttempts || 5
    })
  }

  /**
   * Start email verification process
   * Sends verification code or link to email
   */
  async startEmailVerification(request: EmailVerificationRequest): Promise<{ success: boolean; message: string }> {
    const email = request.email.toLowerCase()

    // Check rate limit
    const canRequest = await this.tokenManager.checkRateLimit(
      email,
      this.config.maxCodeRequests || 5,
      this.config.codeRequestWindow || 3600
    )

    if (!canRequest) {
      throw new ValidationError('Too many verification requests. Please try again later.')
    }

    // Create verification token
    const token = await this.tokenManager.createToken({
      ...request,
      email
    })

    // Send verification code/link based on type
    switch (request.type) {
      case 'verify_email':
      case 'change_email':
        return this.sendVerificationEmail(email, token.code, request.type, request.redirectUrl)

      case 'reset_password':
        return this.sendPasswordResetEmail(email, token.code, request.redirectUrl)

      case 'login_link':
        return this.sendLoginLinkEmail(email, token.code, request.redirectUrl)

      default:
        throw new ValidationError(`Unknown verification type: ${request.type}`)
    }
  }

  /**
   * Verify email with code
   * Returns user ID if verification succeeds
   */
  async verifyEmail(email: string, code: string, type: string): Promise<EmailVerificationResult> {
    try {
      // Verify the code
      const token = await this.tokenManager.verifyCode(email.toLowerCase(), code, type)

      // Check if token is locked from too many attempts
      const isLocked = await this.tokenManager.isTokenLocked(token.id)
      if (isLocked) {
        throw new ValidationError('Too many failed attempts. Please request a new verification code.')
      }

      return {
        verified: true,
        email: email.toLowerCase(),
        userId: token.userId,
        message: 'Email verified successfully',
        expiresAt: token.expiresAt
      }
    } catch (error) {
      if (error instanceof ValidationError || error instanceof NotFoundError) {
        // Increment attempt counter
        const token = await this.tokenManager.getValidToken(email.toLowerCase(), type)
        if (token) {
          await this.tokenManager.incrementAttempts(token.id)
        }
        throw error
      }
      throw error
    }
  }

  /**
   * Sign up with email and password
   * Requires email verification
   */
  async signupWithEmail(
    email: string,
    password: string,
    name?: string
  ): Promise<{ userId: string; email: string; message: string }> {
    email = email.toLowerCase()

    // Validate email
    if (!email.includes('@')) {
      throw new ValidationError('Invalid email format')
    }

    // Validate password strength
    if (!password || password.length < 8) {
      throw new ValidationError('Password must be at least 8 characters long')
    }

    // Check if user already exists
    const existingUser = await this.storage.getUserByEmail(email)
    if (existingUser) {
      throw new ConflictError('Email already registered')
    }

    // Hash password
    const passwordHash = await argon2.hash(password)

    // Create user (not verified yet)
    const user = await this.storage.createUser({
      email,
      name,
      plan: 'free',
      entitlements: ['api.read'],
      metadata: {
        passwordHash,
        emailVerified: false
      }
    })

    // Start email verification
    await this.startEmailVerification({
      email,
      type: 'verify_email',
      metadata: { userId: user.id }
    })

    return {
      userId: user.id,
      email,
      message: 'Signup successful. Please verify your email to continue.'
    }
  }

  /**
   * Login with email and password
   * Only works if email is verified
   */
  async loginWithPassword(email: string, password: string): Promise<{ session: Session; user: User }> {
    email = email.toLowerCase()

    // Get user
    const user = await this.storage.getUserByEmail(email)
    if (!user) {
      throw new AuthenticationError('Invalid email or password')
    }

    // Check if email verified
    if (!user.metadata?.emailVerified) {
      throw new AuthenticationError('Please verify your email before logging in')
    }

    // Verify password
    const passwordHash = user.metadata?.passwordHash
    if (!passwordHash) {
      throw new AuthenticationError('Invalid email or password')
    }

    const isValid = await argon2.verify(passwordHash, password)
    if (!isValid) {
      throw new AuthenticationError('Invalid email or password')
    }

    // Create session
    const session = await this.auth.createSession(user.id)

    return { session, user }
  }

  /**
   * Login with magic link (passwordless)
   * Creates temporary token sent via email
   */
  async sendLoginLink(email: string, redirectUrl?: string): Promise<{ message: string }> {
    email = email.toLowerCase()

    // Get or create user
    let user = await this.storage.getUserByEmail(email)
    if (!user) {
      // Create new user for passwordless login
      user = await this.storage.createUser({
        email,
        plan: 'free',
        entitlements: ['api.read'],
        metadata: { emailVerified: true, passwordless: true }
      })
    }

    // Create login token
    await this.startEmailVerification({
      email,
      type: 'login_link',
      redirectUrl,
      metadata: { userId: user.id }
    })

    return {
      message: 'Login link sent to your email. Please check your inbox.'
    }
  }

  /**
   * Complete passwordless login with token
   */
  async completeLoginLink(email: string, code: string): Promise<EmailLoginResponse> {
    email = email.toLowerCase()

    // Verify login token
    const result = await this.verifyEmail(email, code, 'login_link')
    if (!result.verified) {
      throw new AuthenticationError('Invalid or expired login link')
    }

    // Get user
    const user = await this.storage.getUserByEmail(email)
    if (!user) {
      throw new NotFoundError('User', email)
    }

    // Create session
    const session = await this.auth.createSession(user.id)

    return {
      userId: user.id,
      email,
      sessionId: session.id,
      isNewUser: false
    }
  }

  /**
   * Reset password
   * Step 1: Request reset (sends email with code)
   * Step 2: Verify code and set new password
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    email = email.toLowerCase()

    // Check if user exists
    const user = await this.storage.getUserByEmail(email)
    if (!user) {
      // Don't reveal if email exists (security)
      return { message: 'If the email exists, a reset link has been sent.' }
    }

    // Create reset token
    await this.startEmailVerification({
      email,
      type: 'reset_password',
      metadata: { userId: user.id }
    })

    return { message: 'Password reset link sent to your email.' }
  }

  /**
   * Complete password reset with new password
   */
  async resetPasswordWithCode(email: string, code: string, newPassword: string): Promise<{ message: string }> {
    email = email.toLowerCase()

    // Validate new password
    if (!newPassword || newPassword.length < 8) {
      throw new ValidationError('Password must be at least 8 characters long')
    }

    // Verify reset code
    const result = await this.verifyEmail(email, code, 'reset_password')
    if (!result.verified) {
      throw new AuthenticationError('Invalid or expired reset link')
    }

    // Get user
    const user = await this.storage.getUserByEmail(email)
    if (!user) {
      throw new NotFoundError('User', email)
    }

    // Hash new password
    const passwordHash = await argon2.hash(newPassword)

    // Update user
    await this.storage.updateUser(user.id, {
      metadata: {
        ...user.metadata,
        passwordHash
      }
    })

    return { message: 'Password reset successfully. Please log in with your new password.' }
  }

  /**
   * Mark email as verified
   * Called after email verification code is confirmed
   */
  async markEmailAsVerified(email: string): Promise<void> {
    email = email.toLowerCase()

    const user = await this.storage.getUserByEmail(email)
    if (!user) {
      throw new NotFoundError('User', email)
    }

    await this.storage.updateUser(user.id, {
      metadata: {
        ...user.metadata,
        emailVerified: true
      }
    })
  }

  /**
   * Change email address
   * Requires verification of new email
   */
  async requestEmailChange(userId: string, newEmail: string): Promise<{ message: string }> {
    newEmail = newEmail.toLowerCase()

    // Validate email
    if (!newEmail.includes('@')) {
      throw new ValidationError('Invalid email format')
    }

    // Check if new email is already in use
    const existingUser = await this.storage.getUserByEmail(newEmail)
    if (existingUser && existingUser.id !== userId) {
      throw new ConflictError('Email already in use')
    }

    // Start verification for new email
    await this.startEmailVerification({
      email: newEmail,
      type: 'change_email',
      metadata: { userId }
    })

    return { message: 'Verification link sent to your new email address.' }
  }

  /**
   * Complete email change with verification code
   */
  async completeEmailChange(userId: string, newEmail: string, code: string): Promise<{ message: string }> {
    newEmail = newEmail.toLowerCase()

    // Verify code
    const result = await this.verifyEmail(newEmail, code, 'change_email')
    if (!result.verified || result.userId !== userId) {
      throw new AuthenticationError('Invalid or expired email change link')
    }

    // Update user
    await this.storage.updateUser(userId, { email: newEmail })

    return { message: 'Email changed successfully.' }
  }

  // Private email sending methods

  private async sendVerificationEmail(
    email: string,
    code: string,
    type: string,
    redirectUrl?: string
  ): Promise<{ success: boolean; message: string }> {
    const link = generateVerificationLink(this.config.appUrl, email, code, type, redirectUrl)

    const result = await this.emailProvider.sendVerificationLink(email, link)

    if (!result.success) {
      throw new Error(`Failed to send verification email: ${result.error}`)
    }

    return {
      success: true,
      message: `Verification ${type === 'change_email' ? 'link' : 'code'} sent to ${email}`
    }
  }

  private async sendPasswordResetEmail(
    email: string,
    code: string,
    redirectUrl?: string
  ): Promise<{ success: boolean; message: string }> {
    const link = generateResetLink(this.config.appUrl, email, code, redirectUrl)

    const result = await this.emailProvider.sendPasswordResetLink(email, link)

    if (!result.success) {
      throw new Error(`Failed to send reset email: ${result.error}`)
    }

    return {
      success: true,
      message: `Password reset link sent to ${email}`
    }
  }

  private async sendLoginLinkEmail(
    email: string,
    code: string,
    redirectUrl?: string
  ): Promise<{ success: boolean; message: string }> {
    const link = generateLoginLink(this.config.appUrl, email, code, redirectUrl)

    const result = await this.emailProvider.sendLoginLink(email, link)

    if (!result.success) {
      throw new Error(`Failed to send login link: ${result.error}`)
    }

    return {
      success: true,
      message: `Login link sent to ${email}`
    }
  }
}
